import { getRawCollection } from '@adornis/baseql/server/collections.js';
import * as crypto from 'node:crypto';
import { Submission } from '../submission.js';

export interface LanguageUseRuleResultPatch {
  score?: number;
  explanation?: string;
  confidence?: number;
  reviewerFeedback?: string;
  isApproved?: boolean;
  actualPointsGiven?: number;
}

/**
 * Upsert-by-id into Submission.languageUseRuleResults: merges `patch` onto whatever entry already
 * exists for this LanguageUseScheme group (fields not present in `patch` are carried over unchanged).
 * Shared by the auto-grade path (gradingAgent.ts, full patch after a fresh gradeLanguageUseSchemeGroup
 * call) and the teacher-override mutation (updateLanguageUseRuleResultMutation, partial patch) so neither
 * one silently wipes what the other set.
 *
 * Uses atomic positional/`$push` updates rather than a read-modify-write of the whole array: a
 * submission can have several LanguageUseScheme groups graded concurrently in the same run (one
 * gradeLanguageUseSchemeGroup call per group, all in flight together), and a full-array `$set` built from
 * a stale read would silently drop whichever group's result committed first — exactly the "one group's
 * result vanished from languageUseRuleResults" symptom this replaces.
 */
export async function upsertLanguageUseRuleResult(
  submissionId: string,
  languageUseSchemeId: string,
  patch: LanguageUseRuleResultPatch,
): Promise<void> {
  const rawSubmissions = await getRawCollection(Submission._collectionName);

  // languageUseRuleResults defaults to null (Maybe<LanguageUseRuleResult[]>, see submission.ts) — a submission
  // that hasn't had any group graded yet still has it as null, not []. An arrayFilters update against
  // a non-array field is a hard Mongo error ("Cannot apply array updates to non-array element"), so
  // normalize it to [] first. Idempotent: only matches while the field isn't already an array.
  await rawSubmissions.updateOne(
    { _id: submissionId, languageUseRuleResults: { $not: { $type: 'array' } } } as any,
    { $set: { languageUseRuleResults: [] } } as any,
  );

  const setOps: Record<string, unknown> = {
    'languageUseRuleResults.$[elem].computedAt': new Date().toISOString(),
  };
  if (patch.score !== undefined) setOps['languageUseRuleResults.$[elem].score'] = patch.score;
  if (patch.explanation !== undefined) setOps['languageUseRuleResults.$[elem].explanation'] = patch.explanation;
  if (patch.confidence !== undefined) setOps['languageUseRuleResults.$[elem].confidence'] = patch.confidence;
  if (patch.reviewerFeedback) setOps['languageUseRuleResults.$[elem].reviewerFeedback'] = patch.reviewerFeedback;
  if (patch.isApproved !== undefined) setOps['languageUseRuleResults.$[elem].isApproved'] = patch.isApproved;
  if (patch.actualPointsGiven !== undefined)
    setOps['languageUseRuleResults.$[elem].actualPointsGiven'] = patch.actualPointsGiven;

  const updateResult = await rawSubmissions.updateOne(
    { _id: submissionId } as any,
    { $set: setOps } as any,
    { arrayFilters: [{ 'elem.languageUseSchemeId': languageUseSchemeId }] } as any,
  );

  // matchedCount reflects the top-level {_id} filter, which always matches an existing submission —
  // it does NOT tell us whether the arrayFilters clause found an element to update. modifiedCount is
  // the only reliable signal here, and stays accurate because computedAt is set unconditionally above
  // (so a matched element is never "unmodified" purely because the rest of the patch was a no-op).
  if (updateResult.modifiedCount > 0) return;

  // No existing entry for this group yet — atomically append one, guarded so a concurrent insert for
  // the same (submission, group) pair can't create a duplicate array entry.
  const newEntry = {
    _class: 'LanguageUseRuleResult',
    _id: crypto.randomUUID(),
    languageUseSchemeId,
    score: patch.score ?? 0,
    explanation: patch.explanation ?? '',
    confidence: patch.confidence ?? 0,
    computedAt: new Date().toISOString(),
    ...(patch.reviewerFeedback ? { reviewerFeedback: patch.reviewerFeedback } : {}),
    ...(patch.isApproved !== undefined ? { isApproved: patch.isApproved } : {}),
    ...(patch.actualPointsGiven !== undefined ? { actualPointsGiven: patch.actualPointsGiven } : {}),
  };

  const insertResult = await rawSubmissions.updateOne(
    { _id: submissionId, 'languageUseRuleResults.languageUseSchemeId': { $ne: languageUseSchemeId } } as any,
    { $push: { languageUseRuleResults: newEntry } } as any,
  );

  // Lost the race to a concurrent first-time insert for the same group (matchedCount 0 here, since
  // the $ne filter no longer matches once that other insert landed) — the entry now exists, so retry
  // as a plain field update instead of pushing a second copy.
  if (insertResult.matchedCount === 0) {
    await rawSubmissions.updateOne(
      { _id: submissionId } as any,
      { $set: setOps } as any,
      { arrayFilters: [{ 'elem.languageUseSchemeId': languageUseSchemeId }] } as any,
    );
  }
}

/**
 * Removes every Submission.languageUseRuleResults entry for one LanguageUseScheme, across every submission of
 * the exam — the counterpart to upsertLanguageUseRuleResult, used when a teacher deletes the scheme itself
 * (solo or multi-member; results are unified into this same array for both, so one $pull covers either
 * case).
 */
export async function deleteLanguageUseRuleResultsForScheme(examId: string, languageUseSchemeId: string): Promise<void> {
  const rawSubmissions = await getRawCollection(Submission._collectionName);
  await rawSubmissions.updateMany(
    { examId } as any,
    { $pull: { languageUseRuleResults: { languageUseSchemeId } } } as any,
  );
}
